import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("pending");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= 12) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        if (attemptsRef.current >= 12) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-4" data-testid="billing-success-page">
      <div className="max-w-md w-full bg-white border border-[#E5E2D9] rounded-xl p-8 text-center fade-up">
        {status === "pending" && (
          <>
            <Loader2 size={42} className="mx-auto text-[#1A2F24] animate-spin mb-4" />
            <h1 className="font-display font-bold text-2xl text-[#1A2F24] mb-2">{t("pricing.pending")}</h1>
            <p className="text-sm text-[#5C6B61]">{t("common.loading")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={42} className="mx-auto text-[#1A2F24] mb-4" />
            <h1 className="font-display font-bold text-2xl text-[#1A2F24] mb-2" data-testid="payment-success-title">
              {t("pricing.success_title")}
            </h1>
            <p className="text-sm text-[#5C6B61] mb-6">{t("pricing.success_subtitle")}</p>
            <Button onClick={() => nav("/app")} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white" data-testid="billing-go-dashboard">
              {t("pricing.go_dashboard")}
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired" || status === "timeout") && (
          <>
            <XCircle size={42} className="mx-auto text-[#C05A44] mb-4" />
            <h1 className="font-display font-bold text-2xl text-[#1A2F24] mb-2">{t("pricing.failed")}</h1>
            <Button onClick={() => nav("/app")} variant="outline" className="border-[#1A2F24] text-[#1A2F24]">
              {t("common.back")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
